import { inject, Injectable } from '@angular/core';
import { combineLatest, from, map, Observable, of, switchMap, take } from 'rxjs';
import { AndroidSettings, IOSSettings, NativeSettings } from 'capacitor-native-settings';
import { GEOLOCATION_TOKEN, GeolocationProvider } from '../tokens/geolocation.token';
import { MODAL_TOKEN, ModalProvider } from '../tokens/modal.token';
import { PLATFORM_TOKEN, PlatformProvider } from '../tokens/platform.token';
import { ActionModalComponent, PresentModalComponent } from '../modals';
import { PermissionStates } from '../enums';
import { CoordinatesModel, SelectedLocation } from '../interfaces';
import {
  locationPermissionDeniedText,
  locationServicesOffText,
  locationUnavailableText,
  permissionModalButtons,
  PermissionModalText
} from '../consts';
import { GeocodingService } from './geocoding.service';

type LocationAccessReason = 'services-off' | 'permission-denied' | 'unavailable';

export type LocationAnalyticsData = {
  readonly servicesEnabled: boolean;
  readonly permission: PermissionStates;
};

@Injectable({ providedIn: 'root' })
export class LocationAccessService {
  private readonly geolocationProvider: GeolocationProvider = inject(GEOLOCATION_TOKEN);
  private readonly modalProvider: ModalProvider = inject(MODAL_TOKEN);
  private readonly platformProvider: PlatformProvider = inject(PLATFORM_TOKEN);
  private readonly geocodingService: GeocodingService = inject(GeocodingService);

  private readonly autoAttemptCooldownMs: number = 24 * 60 * 60 * 1000;
  private readonly autoAttemptKey: string = 'location-auto-attempt-at';

  // Auto-próba przy wejściu na ekran - prompt/modal max raz na 24h. Picker omija bramkę.
  ensureLocationOnEntry$(): Observable<SelectedLocation | null> {
    if (!this.canAutoAttempt()) return of(null);
    this.markAutoAttempt();
    return this.ensureLocation$();
  }

  accessState$(): Observable<LocationAnalyticsData> {
    return combineLatest([
      this.geolocationProvider.isLocationServicesEnabled(),
      this.geolocationProvider.checkPermissionState()
    ]).pipe(
      take(1),
      map(([servicesEnabled, permission]: [boolean, PermissionStates]) => ({ servicesEnabled, permission }))
    );
  }

  // Pełny przepływ GPS: prompt (PROMPT) lub modal (DENIED/services-off). Zwraca lokalizację albo null.
  ensureLocation$(): Observable<SelectedLocation | null> {
    return this.geolocationProvider.isLocationServicesEnabled().pipe(
      take(1),
      switchMap((enabled: boolean) => {
        if (!enabled) {
          this.showAccessModal('services-off');
          return of(null);
        }

        return this.geolocationProvider.checkPermissionState().pipe(
          take(1),
          switchMap((state: PermissionStates) => this.resolveByState(state))
        );
      })
    );
  }

  private canAutoAttempt(): boolean {
    const raw: string | null = sessionStorage.getItem(this.autoAttemptKey);
    if (!raw) return true;

    const last: number = Number(raw);
    if (Number.isNaN(last)) return true;

    return Date.now() - last >= this.autoAttemptCooldownMs;
  }

  private markAutoAttempt(): void {
    sessionStorage.setItem(this.autoAttemptKey, String(Date.now()));
  }

  private resolveByState(state: PermissionStates): Observable<SelectedLocation | null> {
    if (state === PermissionStates.GRANTED) return this.acquireLocation();

    if (state === PermissionStates.DENIED) {
      this.showAccessModal('permission-denied');
      return of(null);
    }

    return from(this.geolocationProvider.requestPermissions()).pipe(
      switchMap((granted: boolean) => {
        if (!granted) {
          this.showAccessModal('permission-denied');
          return of(null);
        }
        return this.acquireLocation();
      })
    );
  }

  private acquireLocation(): Observable<SelectedLocation | null> {
    return this.geolocationProvider.getCoordinates().pipe(
      take(1),
      switchMap((coordinates: CoordinatesModel | null) => {
        if (!coordinates) {
          // GRANTED ale brak fixa (timeout) - nie problem uprawnień.
          this.showAccessModal('unavailable');
          return of(null);
        }

        return this.geocodingService.reverseGeocode(coordinates).pipe(
          map((name: string | null) => {
            const location: SelectedLocation = {
              name: name ?? 'Moja lokalizacja',
              coordinates,
              source: 'auto'
            };
            return location;
          })
        );
      })
    );
  }

  private showAccessModal(reason: LocationAccessReason): void {
    if (reason === 'unavailable') {
      this.modalProvider
        .showModal$({
          component: PresentModalComponent,
          cssClass: 'present-modal',
          componentProps: {
            header: locationUnavailableText.header,
            message: locationUnavailableText.message,
            btnTxt: 'Zamknij'
          }
        })
        .pipe(take(1))
        .subscribe();
      return;
    }

    const config: { text: PermissionModalText; action: () => void } =
      reason === 'services-off'
        ? { text: locationServicesOffText, action: () => this.openLocationServicesSettings() }
        : { text: locationPermissionDeniedText, action: () => this.openAppSettings() };

    this.modalProvider
      .showModal$({
        component: ActionModalComponent,
        cssClass: 'present-modal',
        componentProps: {
          header: config.text.header,
          message: config.text.message,
          btnOk: permissionModalButtons.ok,
          btnCancel: permissionModalButtons.cancel,
          action: config.action
        }
      })
      .pipe(take(1))
      .subscribe();
  }

  private openLocationServicesSettings(): void {
    if (!this.platformProvider.isNativeRun) return;

    if (this.platformProvider.isAndroid) {
      NativeSettings.openAndroid({ option: AndroidSettings.Location });
    } else {
      NativeSettings.openIOS({ option: IOSSettings.LocationServices });
    }
  }

  private openAppSettings(): void {
    if (!this.platformProvider.isNativeRun) return;

    if (this.platformProvider.isAndroid) {
      NativeSettings.openAndroid({ option: AndroidSettings.ApplicationDetails });
    } else {
      NativeSettings.openIOS({ option: IOSSettings.App });
    }
  }
}
